import { graphql, useStaticQuery } from 'gatsby'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getPostsByLanguage } from '../helpers/get-posts-by-lng'

const PostsContext = createContext('')

const PostsProvider = ({ children }) => {
  const [posts, setPosts] = useState({})
  const data = useGetAllPosts()
  const { i18n } = useTranslation()

  useEffect(() => {
    const all = [
      ...data.allWpEvent.edges,
      ...data.allWpUniversityPost.edges,
      ...data.allWpProductSingleNews.edges,
      ...data.allWpReportsAndanalysis.edges,
      ...data.allWpCompanySingleNews.edges,
      ...data.allWpNftStory.edges,
    ]
    const postsFiltered = getPostsByLanguage(all, i18n.language)
    setPosts({
      all,
      events: data.allWpEvent.edges,
      university: data.allWpUniversityPost.edges,
      productNews: data.allWpProductSingleNews.edges,
      marketUpdates: data.allWpMarketUpdate.edges,
      research: data.allWpReportsAndanalysis.edges,
      nftStories: data.allWpNftStory.edges,
    })
  }, [i18n.language, data])

  const state = posts

  return <PostsContext.Provider value={state}>{children}</PostsContext.Provider>
}

const usePosts = () => {
  const context = useContext(PostsContext)

  if (!context) {
    throw new Error('useLocale must be used within an PostsProvider')
  }

  return context
}

export { PostsProvider, usePosts }

const useGetAllPosts = () => {
  const posts = useStaticQuery(graphql`
    {
      allWpEvent {
        edges {
          node {
            id
            title
            slug
            content
            excerpt
            uri
            date(formatString: "MMM DD, YYYY")
            dateGmt
            categories {
              nodes {
                name
                slug
                description
              }
            }
            tags {
              nodes {
                name
              }
            }
            acf: acfEvents {
              eventDatetime {
                endDate
                endTime
                fieldGroupName
                startDate
                startTime
              }
              eventLocation
              readingDuration
            }
            featuredImage {
              node {
                id
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      width: 806
                      placeholder: BLURRED
                      formats: [AUTO]
                    )
                  }
                }
              }
            }
            locale {
              locale
              id
            }
          }
        }
      }
      allWpUniversityPost {
        edges {
          node {
            id
            title
            slug
            content
            excerpt
            uri
            date(formatString: "MMM DD, YYYY")
            dateGmt
            categories {
              nodes {
                name
                slug
                description
              }
            }
            tags {
              nodes {
                name
                slug
              }
            }
            featuredImage {
              node {
                id
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      width: 806
                      placeholder: BLURRED
                      formats: [AUTO]
                    )
                  }
                }
              }
            }
            locale {
              locale
              id
            }
          }
        }
      }
      allWpProductSingleNews {
        edges {
          node {
            id
            title
            slug
            content
            excerpt
            uri
            date(formatString: "MMM DD, YYYY")
            dateGmt
            categories {
              nodes {
                name
                slug
                description
              }
            }
            tags {
              nodes {
                name
                slug
              }
            }
            featuredImage {
              node {
                id
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      width: 806
                      placeholder: BLURRED
                      formats: [AUTO]
                    )
                  }
                }
              }
            }
            locale {
              locale
              id
            }
          }
        }
      }
      allWpReportsAndanalysis {
        edges {
          node {
            id
            title
            slug
            content
            excerpt
            uri
            date(formatString: "MMM DD, YYYY")
            dateGmt
            categories {
              nodes {
                name
                slug
                description
              }
            }
            tags {
              nodes {
                name
                slug
              }
            }
            featuredImage {
              node {
                id
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      width: 806
                      placeholder: BLURRED
                      formats: [AUTO]
                    )
                  }
                }
              }
            }
            locale {
              locale
              id
            }
          }
        }
      }
      allWpCompanySingleNews {
        edges {
          node {
            id
            title
            slug
            content
            excerpt
            uri
            date(formatString: "MMM DD, YYYY")
            dateGmt
            categories {
              nodes {
                name
                slug
                description
              }
            }
            tags {
              nodes {
                name
                slug
              }
            }
            featuredImage {
              node {
                id
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      width: 806
                      placeholder: BLURRED
                      formats: [AUTO]
                    )
                  }
                }
              }
            }
            locale {
              locale
              id
            }
          }
        }
      }
      allWpMarketUpdate {
        edges {
          node {
            id
            title
            slug
            content
            excerpt
            uri
            date(formatString: "MMM DD, YYYY")
            dateGmt
            categories {
              nodes {
                id
                slug
              }
            }
            tags {
              nodes {
                name
                slug
              }
            }
            acf: acfMarketUpdates {
              keywords
              readingDuration
              dateRange {
                dateFrom
                dateTo
              }
            }
            featuredImage {
              node {
                id
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      width: 806
                      placeholder: BLURRED
                      formats: [AUTO]
                    )
                  }
                }
              }
            }
          }
        }
      }
      allWpNftStory {
        edges {
          node {
            id
            title
            slug
            nodeType
            content
            excerpt
            uri
            date(formatString: "MMM DD, YYYY")
            dateGmt
            categories {
              nodes {
                name
                slug
                description
              }
            }
            acf: acfNftStories {
              readingDuration
            }
            featuredImage {
              node {
                id
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      width: 806
                      placeholder: BLURRED
                      formats: [AUTO]
                    )
                  }
                }
              }
            }
          }
        }
      }
    }
  `)

  return posts
}
